import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Plus, FunnelSimple, FileArrowDown, FileXls } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import ExpenseDetail from "@/pages/ExpenseDetail";
import { useAuth } from "@/contexts/AuthContext";

const HUB_COLS = ["Date", "Bill #", "Category", "Vendor", "Stage", "Status", "Approved By", "UTR", "Amount"];
const FULL_COLS = ["Date", "Hub", "Bill #", "Category", "Vendor", "Submitter", "Stage", "Status", "Approved By", "UTR", "Amount"];

const fmtInr = (n) => "₹" + Math.round(n || 0).toLocaleString("en-IN");

const stageLabel = (status) =>
  ["pending", "correction_requested"].includes(status) ? "Manager" :
    status === "approved" ? "Finance" : "—";

function FiltersBar({ q, setQ, status, setStatus, category, setCategory, hub, setHub, month, setMonth, cats, hubs, showHubFilter }) {
  return (
    <div className="flex flex-wrap items-center gap-2 p-4 border-b border-zinc-200">
      <div className="relative flex-1 min-w-[220px]">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input data-testid="expense-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search hub, bill #, vendor, submitter…" className="pl-9" />
      </div>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="filter-status" className="w-[150px]"><FunnelSimple size={14} className="mr-1.5" /><SelectValue placeholder="Status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
          <SelectItem value="approved">Approved</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
          <SelectItem value="paid">Paid</SelectItem>
          <SelectItem value="correction_requested">Correction requested</SelectItem>
        </SelectContent>
      </Select>
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger data-testid="filter-category" className="w-[180px]"><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="all">All categories</SelectItem>
          {cats.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
        </SelectContent>
      </Select>
      {showHubFilter && (
        <Select value={hub} onValueChange={setHub}>
          <SelectTrigger data-testid="filter-hub" className="w-[150px]"><SelectValue placeholder="Hub" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All hubs</SelectItem>
            {hubs.map((h) => (<SelectItem key={h} value={h}>{h}</SelectItem>))}
          </SelectContent>
        </Select>
      )}
      <Input type="month" data-testid="filter-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[160px]" />
    </div>
  );
}

function ExpensesTable({ items, loading, isHubUser, onOpen }) {
  const cols = isHubUser ? HUB_COLS : FULL_COLS;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-200">
            {cols.map((c) => (
              <th key={c} className={`px-4 py-2.5 font-medium ${c === "Amount" ? "text-right" : c === "Status" ? "text-center" : ""}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && !loading && (
            <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-zinc-500">No expenses found.</td></tr>
          )}
          {items.map((e) => (
            <tr key={e.id} data-testid={`expense-row-${e.id}`}
                onClick={() => onOpen(e.id)}
                className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer">
              <td className="px-4 py-3 font-mono text-zinc-700 text-xs">{e.expense_date}</td>
              {!isHubUser && <td className="px-4 py-3 text-zinc-700">{e.hub || "—"}</td>}
              <td className="px-4 py-3 font-mono text-zinc-700 text-xs">{e.bill_number}</td>
              <td className="px-4 py-3 text-zinc-700">{e.category}</td>
              <td className="px-4 py-3 text-zinc-700">{e.vendor_name}</td>
              {!isHubUser && <td className="px-4 py-3 text-zinc-900">{e.submitter_name}</td>}
              <td className="px-4 py-3">
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  {stageLabel(e.status)}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                <StatusBadge status={e.status === "correction_requested" ? "pending" : e.status} testId={`status-${e.id}`} />
              </td>
              <td className="px-4 py-3">
                {e.manager_approver_name ? (
                  <div data-testid={`approved-by-${e.id}`}>
                    <div className="text-xs text-zinc-800">{e.manager_approver_name}</div>
                    {e.approved_at && (
                      <div className="font-mono text-[10px] text-zinc-500">
                        {new Date(e.approved_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-zinc-400">—</span>
                )}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-zinc-600">{e.utr || "—"}</td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-900">{fmtInr(e.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Expenses({ scope = "all", title = "All expenses" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [hub, setHub] = useState("all");
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const isHubUser = user?.role === "hub_user";
  const canExport = user && ["manager", "finance", "admin"].includes(user.role);

  const fetchList = useCallback(async () => {
    setLoading(true);
    const params = { scope };
    if (status !== "all") params.status = status;
    if (category !== "all") params.category = category;
    if (hub !== "all") params.hub = hub;
    if (month) params.month = month;
    if (q) params.q = q;
    const { data } = await api.get("/expenses", { params });
    setItems(data);
    setLoading(false);
  }, [scope, status, category, hub, month, q]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);
  useEffect(() => {
    if (user && !isHubUser) {
      api.get("/hubs").then(({ data }) => setHubs(data.map((h) => h.name)));
    }
  }, [user, isHubUser]);

  const downloadReport = async (path, filename) => {
    const token = localStorage.getItem("sohom_token");
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (category !== "all") params.set("category", category);
    if (hub !== "all") params.set("hub", hub);
    if (month) params.set("month", month);
    const res = await fetch(`${API_URL}${path}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCsv = () => downloadReport("/expenses/export.csv", "sohom-expenses.csv");
  const downloadExcel = () => downloadReport("/reports/excel", "sohom-expense-report.xlsx");

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Records</div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-900">{title}</h1>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <>
              <Button variant="outline" onClick={downloadCsv} data-testid="export-csv-btn" className="border-zinc-300">
                <FileArrowDown size={16} className="mr-1.5" /> CSV
              </Button>
              <Button variant="outline" onClick={downloadExcel} data-testid="export-excel-btn"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <FileXls size={16} className="mr-1.5" /> Excel report
              </Button>
            </>
          )}
          <Button onClick={() => nav("/expenses/new")} data-testid="new-expense-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus size={16} className="mr-1.5" /> New expense
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg">
        <FiltersBar
          q={q} setQ={setQ}
          status={status} setStatus={setStatus}
          category={category} setCategory={setCategory}
          hub={hub} setHub={setHub}
          month={month} setMonth={setMonth}
          cats={cats} hubs={hubs}
          showHubFilter={!isHubUser}
        />
        <ExpensesTable items={items} loading={loading} isHubUser={isHubUser} onOpen={setOpenId} />
        {loading && <div className="p-4 text-xs text-zinc-500 font-mono">Loading…</div>}
      </div>

      <ExpenseDetail expenseId={openId} onClose={() => setOpenId(null)} onChanged={fetchList} role={user?.role} />
    </DashboardLayout>
  );
}
